/**
 * Sync Queue Service
 * Manages offline operation queuing and FIFO replay for the BNFT application.
 *
 * Operations that fail due to network unavailability are stored in IndexedDB
 * and automatically replayed when connectivity is restored.
 *
 * This service uses a raw axios instance (not the intercepted api client) to
 * avoid circular dependency issues and to prevent re-queuing of replayed requests.
 */

import axios from 'axios';

import { getReplayAuthHeaders, renewReplayAuthHeaders } from './authToken';
import {
  enqueueSyncItem,
  getAllSyncItems,
  getPendingSyncCount,
  removeSyncItem,
  updateSyncItem,
  type SyncQueueItem,
} from './offlineDb';
import { getApiBaseUrl } from '../config/env';

export type { SyncQueueItem };

// ─── Image helpers ─────────────────────────────────────────────────────────────

export interface QueuedImage {
  data: string;      // base64-encoded file content
  mimeType: string;
  fileName: string;
}

/** Convert a File/Blob to a base64-encoded string */
export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve((reader.result as string).split(',')[1] ?? '');
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/** Convert a base64 string back to a Blob */
function base64ToBlob(base64: string, mimeType: string): Blob {
  const bytes = atob(base64);
  const buffer = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) {
    buffer[i] = bytes.charCodeAt(i);
  }
  return new Blob([buffer], { type: mimeType });
}

/** Replay uses a dedicated axios instance to avoid circular imports with api.ts */
const replayClient = axios.create({
  timeout: 30_000,
  // Required for the HttpOnly session cookies to travel with a replayed write.
  // Without it the browser withholds them and every replay returns 401.
  withCredentials: true,
});

// ─── Enqueue ───────────────────────────────────────────────────────────────────

/**
 * Add an operation to the sync queue.
 * Also registers a Background Sync event with the Service Worker when supported.
 * Returns the auto-generated IndexedDB id of the new item.
 */
export async function enqueue(item: Omit<SyncQueueItem, 'id'>): Promise<number> {
  const id = await enqueueSyncItem(item);

  // Register a Background Sync tag so the SW can replay even if the tab is closed
  try {
    if ('serviceWorker' in navigator) {
      const registration = await navigator.serviceWorker.ready;
      if ('sync' in registration) {
        await (
          registration as ServiceWorkerRegistration & {
            sync: { register(tag: string): Promise<void> };
          }
        ).sync.register('bnft-sync-queue');
      }
    }
  } catch {
    // Background Sync API not supported — NetworkContext fallback is sufficient
  }

  return id;
}

// ─── Read ──────────────────────────────────────────────────────────────────────

/** Get all items in the sync queue (pending, processing, and failed). */
export async function getAll(): Promise<SyncQueueItem[]> {
  return getAllSyncItems();
}

/** Get the count of items with status 'pending'. */
export async function getPendingCount(): Promise<number> {
  return getPendingSyncCount();
}

// ─── Write ─────────────────────────────────────────────────────────────────────

/** Remove an item from the queue by its IndexedDB id. */
export async function remove(id: number): Promise<void> {
  return removeSyncItem(id);
}

/** Update the status and retryCount of a queued item. */
export async function updateStatus(
  id: number,
  status: SyncQueueItem['status'],
  retryCount: number
): Promise<void> {
  const items = await getAllSyncItems();
  const item = items.find((i) => i.id === id);
  if (item) {
    await updateSyncItem({ ...item, status, retryCount });
  }
}

// ─── Process Queue ─────────────────────────────────────────────────────────────

export interface ProcessQueueResult {
  success: number;
  failed: number;
  remaining: number;
  /** True when replay stopped because the session needs re-authentication. */
  authRequired: boolean;
  /** True when replay stopped because connectivity was lost mid-run. */
  networkLost: boolean;
}

/**
 * Strip any (legacy) frozen credential header so the fresh one always wins.
 *
 * Older queue entries — recorded before credentials stopped being captured —
 * may still carry an Authorization header, now long expired. A stale CSRF
 * header would be just as wrong.
 */
function stripAuthHeader(headers?: Record<string, string>): Record<string, string> {
  if (!headers) {return {};}
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(headers)) {
    const lower = key.toLowerCase();
    if (lower !== 'authorization' && lower !== 'x-csrf-token') {out[key] = value;}
  }
  return out;
}

/**
 * Replay all pending operations in FIFO order (sorted by timestamp).
 *
 * Credentials are obtained fresh for each item, never frozen into the queue
 * entry: the CSRF header in cookie mode, a renewed Bearer token otherwise. A
 * queued write can wait days for connectivity, far past the 30-minute access
 * token lifetime — freezing one is what used to flood the history with
 * "invalid or expired token" failures.
 *
 * Stop conditions:
 * - Network lost          → remaining items stay pending (`networkLost`).
 * - Auth dead (refresh KO)→ remaining items stay pending (`authRequired`); the
 *                           user must reconnect. Items are never marked failed.
 * - 409 Conflict          → treated as idempotent success (already applied).
 * - Other 4xx/5xx         → retryCount++; marked 'failed' once maxRetries hit.
 */
export async function processQueue(): Promise<ProcessQueueResult> {
  const allItems = await getAllSyncItems();

  // Process only pending items, in chronological order (FIFO)
  const pending = allItems
    .filter((i) => i.status === 'pending')
    .sort((a, b) => a.timestamp - b.timestamp);

  let success = 0;
  let failed = 0;
  let authRequired = false;
  let networkLost = false;

  for (const item of pending) {
    // Stop if we've lost connectivity again
    if (!navigator.onLine) {
      networkLost = true;
      break;
    }

    // Obtain fresh credentials for the current scheme (CSRF header in cookie
    // mode, Bearer otherwise), renewing the session if needed.
    let authHeaders = await getReplayAuthHeaders();
    if (!authHeaders) {
      // No way to authenticate (session dead). Keep the item pending — it is
      // NOT lost — and stop. The user must reconnect / log in.
      await updateSyncItem({ ...item, status: 'pending' });
      authRequired = true;
      break;
    }

    // Mark as processing so the UI can show progress
    await updateSyncItem({ ...item, status: 'processing' });

    const baseURL = getApiBaseUrl();

    const sendRequest = (headers: Record<string, string>) =>
      replayClient.request<{ event?: { id: number } }>({
        baseURL,
        url: item.url,
        method: item.method,
        data: item.body,
        headers: {
          'Content-Type': 'application/json',
          ...stripAuthHeader(item.headers),
          ...headers,
          // Flag that prevents the error interceptor in api.ts from re-queuing
          'X-Replay': 'true',
        },
      });

    try {
      let response: Awaited<ReturnType<typeof sendRequest>> | undefined;
      try {
        response = await sendRequest(authHeaders);
      } catch (err) {
        // 401: the access credential lapsed between validation and send (clock
        // skew, latency).
        // 403: the CSRF token is missing or stale — typically a session opened
        // before the migration, which never received one.
        // Both recover the same way: renew once and retry the same item.
        const status = axios.isAxiosError(err) ? err.response?.status : undefined;
        if (status === 401 || status === 403) {
          const renewed = await renewReplayAuthHeaders();
          if (!renewed) {
            await updateSyncItem({ ...item, status: 'pending' });
            authRequired = true;
            break;
          }
          authHeaders = renewed;
          response = await sendRequest(renewed);
        } else {
          throw err;
        }
      }

      // After a successful event creation, upload any queued images from metadata
      if (item.operationType === 'checkpoint_event_create') {
        const eventId = response?.data?.event?.id;
        const queuedImages = (item.metadata as { images?: QueuedImage[] } | null)?.images;
        if (eventId && queuedImages?.length) {
          for (const img of queuedImages) {
            try {
              const blob = base64ToBlob(img.data, img.mimeType);
              const formData = new FormData();
              formData.append('file', blob, img.fileName);
              await replayClient.post(
                `${baseURL}/checkpoint-events/${eventId}/images`,
                formData,
                {
                  headers: {
                    'Content-Type': 'multipart/form-data',
                    ...authHeaders,
                    'X-Replay': 'true',
                  },
                }
              );
            } catch {
              // Non-blocking: image upload failure shouldn't prevent event creation success
            }
          }
        }
      }

      // Request succeeded → remove from queue
      if (item.id !== undefined) {
        await removeSyncItem(item.id);
      }
      success++;

      // Fire-and-forget: persist sync log to backend
      void logSyncResult(item, 'SUCCESS', null, baseURL, authHeaders);
    } catch (err: unknown) {
      // Determine whether the failure is a network error or a server error
      const axiosErr = axios.isAxiosError(err) ? err : null;
      const httpStatus = axiosErr?.response?.status;
      const isNetworkError =
        axiosErr?.code === 'ERR_NETWORK' ||
        axiosErr?.code === 'ECONNABORTED' ||
        axiosErr?.code === 'ERR_CANCELED' ||
        !navigator.onLine;

      if (isNetworkError) {
        // Network gone — restore item to pending and stop processing
        await updateSyncItem({ ...item, status: 'pending' });
        networkLost = true;
        break;
      }

      // Auth failure that survived a refresh attempt → stop, keep the item
      // pending, do NOT mark it failed and do NOT spam the failure log. The
      // session needs re-authentication; the op syncs once the user reconnects.
      if (httpStatus === 401 || httpStatus === 403) {
        await updateSyncItem({ ...item, status: 'pending' });
        authRequired = true;
        break;
      }

      // 409 Conflict = the operation was already applied (idempotent success).
      // This happens when the original request partially succeeded before the
      // network dropped, so the queued replay is a duplicate. Treat as success.
      if (httpStatus === 409) {
        if (item.id !== undefined) {
          await removeSyncItem(item.id);
        }
        success++;
        void logSyncResult(item, 'SUCCESS', '409 conflict (already applied)', baseURL, authHeaders);
        continue;
      }

      // Server-side or application error — increment retryCount
      const newRetryCount = item.retryCount + 1;
      if (newRetryCount >= item.maxRetries) {
        await updateSyncItem({ ...item, status: 'failed', retryCount: newRetryCount });
      } else {
        await updateSyncItem({ ...item, status: 'pending', retryCount: newRetryCount });
      }
      failed++;

      // Fire-and-forget: persist sync failure log to backend
      const errMsg = axiosErr?.response?.data
        ? JSON.stringify(axiosErr.response.data).slice(0, 500)
        : (err instanceof Error ? err.message : 'Unknown error');
      void logSyncResult(item, 'FAILED', errMsg, baseURL, authHeaders);
    }
  }

  const remaining = await getPendingSyncCount();
  return { success, failed, remaining, authRequired, networkLost };
}

/**
 * Reset all 'failed' items back to 'pending' (retryCount 0) so they are retried
 * on the next replay. Used by the "retry all" action to recover items that were
 * wrongly failed by the old expired-token bug. Returns the number reset.
 */
export async function resetFailedToPending(): Promise<number> {
  const items = await getAllSyncItems();
  const failedItems = items.filter((i) => i.status === 'failed');
  for (const item of failedItems) {
    await updateSyncItem({ ...item, status: 'pending', retryCount: 0 });
  }
  return failedItems.length;
}

// ─── Sync Log Persistence ─────────────────────────────────────────────────────

/** Map syncQueue operationType strings to backend enum values. */
const OPERATION_TYPE_MAP: Record<string, string> = {
  checkpoint_arrival: 'CHECKPOINT_ARRIVAL',
  checkpoint_departure: 'CHECKPOINT_DEPARTURE',
  checkpoint_departure_confirm: 'CHECKPOINT_DEPARTURE_CONFIRM',
  checkpoint_event_create: 'CHECKPOINT_EVENT_CREATE',
  checkpoint_event_update: 'CHECKPOINT_EVENT_UPDATE',
};

/**
 * Persist a sync result to the backend (fire-and-forget).
 * Non-critical: failures are silently ignored.
 */
async function logSyncResult(
  item: SyncQueueItem,
  status: 'SUCCESS' | 'FAILED',
  errorMessage: string | null,
  baseURL: string,
  authHeaders: Record<string, string>,
): Promise<void> {
  try {
    await replayClient.post(
      `${baseURL}/offline-sync-logs/`,
      {
        operation_type: OPERATION_TYPE_MAP[item.operationType] ?? item.operationType,
        endpoint_url: item.url,
        original_timestamp: new Date(item.timestamp).toISOString(),
        synced_at: new Date().toISOString(),
        status,
        error_message: errorMessage,
        sync_metadata: item.metadata && typeof item.metadata === 'object'
          ? (() => {
              // Strip base64 image data from metadata to avoid sending large payloads
              const meta = { ...(item.metadata as Record<string, unknown>) };
              if (Array.isArray(meta.images)) {
                meta.images = (meta.images as Array<Record<string, unknown>>).map((img) => ({
                  fileName: img.fileName,
                  mimeType: img.mimeType,
                }));
              }
              return meta;
            })()
          : null,
      },
      {
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders,
          'X-Replay': 'true',
        },
      },
    );
  } catch {
    // Non-critical — sync log persistence failure should never block the queue
  }
}
